/**
 * The design in the controls, kept in this browser so it survives the page going away.
 *
 * Two journeys lost it before this existed, and one of them lost a sale: a reload, and
 * backing out of checkout. Stripe's `cancel_url` loads a fresh page, and a fresh page
 * used to open on the default shed — so a shopper who stepped back from paying to
 * change one thing came back to find their design gone. Both are the same fix: the
 * spec is written here on every change and read back when the store is created.
 *
 * **It is the shopper's draft, not a record of anything.** No purchase reads it — a
 * purchase files its own spec under its checkout id (`purchase/store.ts`) — and nothing
 * leaves the browser (DESIGN §9). So it carries no history and no timestamp: one
 * design, the last one, overwritten in place.
 *
 * **A draft is built by today's engine.** A design saved before a deploy carries the
 * spec version it was written at, and the engine would build that version's shed
 * (DESIGN §6) — which is right for a plan somebody bought and wrong for one they have
 * not: it would quietly sell a new customer a building the engine has since stopped
 * drawing. Nothing was sold under the old version, so there is nothing to keep, and the
 * restore stamps `SPEC_VERSION` on it.
 *
 * **What it cannot read, it drops rather than guesses at.** A field missing from the
 * saved design takes the default shed's value, which is how a spec that gained a field
 * in a later deploy still opens. A field present with the wrong type throws the whole
 * record away: the controls index into the spec without asking, and one string where a
 * length should be is a configurator that does not render.
 */

import { SPEC_VERSION, type Opening, type Spec } from "@planforge/wasm";

import { defaultSpec } from "../engine/spec.js";

const DESIGN_KEY = "parametricplans.design.v1";

/** The shape of the record around the spec. A shape with no version is unchangeable. */
export const DESIGN_RECORD_VERSION = 1;

interface SavedDesign {
  version: number;
  spec: Spec;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * `saved`, read against `template`: every key the template has, from `saved` where it
 * is there and of the same kind, from the template where it is missing. `undefined`
 * when any of it is there and the wrong kind. Keys the template does not have are left
 * behind — a field this build does not know is not one it can show or send.
 */
function overlay(template: unknown, saved: unknown): unknown {
  if (saved === undefined) {
    return template;
  }
  if (typeof template === "number") {
    // Every number in a spec is a whole count: sixteenths, or a pitch's rise and run.
    return Number.isInteger(saved) ? saved : undefined;
  }
  if (typeof template === "string") {
    return typeof saved === "string" ? saved : undefined;
  }
  if (isRecord(template)) {
    if (!isRecord(saved)) {
      return undefined;
    }
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(template)) {
      const read = overlay(value, saved[key]);
      if (read === undefined) {
        return undefined;
      }
      out[key] = read;
    }
    return out;
  }
  return undefined;
}

/**
 * Every field an opening must have; `doorStyle`, `hand`, `swing` and `ramp` are the ones
 * that may be absent.
 */
const OPENING_TEMPLATE: Omit<Opening, "doorStyle" | "hand" | "swing" | "ramp"> = {
  id: "",
  wall: "front",
  type: "door",
  width: 0 as Opening["width"],
  height: 0 as Opening["height"],
  sill: 0 as Opening["sill"],
  offset: 0 as Opening["offset"],
};

/**
 * One saved opening, or `undefined`. Unlike the rest of a spec, an opening has no
 * default to fall back on — a door with no width is not a door — so every field must be
 * there.
 */
function readOpening(saved: unknown): Opening | undefined {
  if (!isRecord(saved)) {
    return undefined;
  }
  for (const key of Object.keys(OPENING_TEMPLATE)) {
    if (saved[key] === undefined) {
      return undefined;
    }
  }
  const read = overlay(OPENING_TEMPLATE, saved) as Opening | undefined;
  if (read === undefined) {
    return undefined;
  }
  // The optional ones: kept when they are there and a word, the record dropped when one
  // is there and not.
  let opening: Opening = read;
  for (const key of ["doorStyle", "hand", "swing"] as const) {
    const value = saved[key];
    if (value === undefined) {
      continue;
    }
    if (typeof value !== "string") {
      return undefined;
    }
    opening = { ...opening, [key]: value };
  }
  // A ramp is a yes or nothing at all: kept when it is `true`, and the record dropped when
  // it is there and not a boolean. `false` is read as no ramp, which is what it says.
  const ramp = saved.ramp;
  if (ramp !== undefined) {
    if (typeof ramp !== "boolean") {
      return undefined;
    }
    if (ramp) {
      opening = { ...opening, ramp };
    }
  }
  return opening;
}

/**
 * A saved spec, made into one this build can show and the engine will build at today's
 * version — or `undefined` if any of it is the wrong kind. Pure, so the rule is tested
 * without a browser.
 *
 * The values themselves are not checked here. A width the validator refuses, or a
 * siding a later engine no longer carries, is a design the engine says no to in words,
 * on the screen built for exactly that (`engine/standing.ts`); restating its limits
 * here would be a second validator.
 */
export function readSavedSpec(saved: unknown): Spec | undefined {
  if (!isRecord(saved) || saved["kind"] !== "shed") {
    return undefined;
  }
  const { openings: defaultOpenings, ...template } = defaultSpec();
  const { openings: savedOpenings, ...rest } = saved;

  let body = overlay(template, rest) as Omit<Spec, "openings"> | undefined;
  if (body === undefined) {
    return undefined;
  }
  // The roof's optional one, which the default shed - a gable - has no value for: a
  // saltbox's ridge position, kept when it is there and a whole number, the record
  // dropped when it is there and not.
  const savedRidge = isRecord(rest["roof"]) ? rest["roof"]["ridgeTwelfths"] : undefined;
  if (savedRidge !== undefined) {
    if (!Number.isInteger(savedRidge)) {
      return undefined;
    }
    body = { ...body, roof: { ...body.roof, ridgeTwelfths: savedRidge as number } };
  }

  let openings: Opening[];
  if (savedOpenings === undefined) {
    openings = defaultOpenings;
  } else if (Array.isArray(savedOpenings)) {
    const read = savedOpenings.map(readOpening);
    if (read.some((opening) => opening === undefined)) {
      return undefined;
    }
    openings = read as Opening[];
  } else {
    return undefined;
  }

  return { ...body, version: SPEC_VERSION, openings };
}

/**
 * The design this browser was last working on, or `undefined` for none.
 *
 * Never throws: storage is denied outright in some privacy modes, and a configurator
 * that failed to open because it could not read a draft would be worse than one that
 * opened on the default shed.
 */
export function loadDesign(): Spec | undefined {
  try {
    const raw = window.localStorage.getItem(DESIGN_KEY);
    if (raw === null) {
      return undefined;
    }
    const stored: unknown = JSON.parse(raw);
    if (!isRecord(stored) || stored["version"] !== DESIGN_RECORD_VERSION) {
      return undefined;
    }
    return readSavedSpec(stored["spec"]);
  } catch {
    return undefined;
  }
}

/** What the configurator opens on, and whether it is the shopper's own last design. */
export interface OpenedDesign {
  spec: Spec;
  /**
   * Whether `spec` is the design this browser was last working on — a restored one, or
   * the default shed when nothing was ever kept because nothing was ever changed. False
   * only when that cannot be said: storage that will not keep anything, or a record
   * that was there and could not be read, so the default shed stands in for a design
   * that was lost.
   */
  isLastDesign: boolean;
}

/**
 * The design the configurator opens on, written back at once.
 *
 * Written even when it is the default shed. A save happens on a change, so a shopper
 * who bought the default unedited had nothing in storage, and the cancel return could
 * not tell "nothing was changed" from "nothing could be kept" — and told them their
 * design was lost when it was on screen. The write is also the test of the second: a
 * browser that refuses it is one whose next page will not have the design either.
 */
export function openDesign(): OpenedDesign {
  const restored = loadDesign();
  if (restored !== undefined) {
    // Restamped at today's version, so the record matches what is on screen.
    saveDesign(restored);
    return { spec: restored, isLastDesign: true };
  }
  const lost = hasRecord();
  const spec = defaultSpec();
  return { spec, isLastDesign: saveDesign(spec) && !lost };
}

/** Whether any record is stored under the key, readable or not. */
function hasRecord(): boolean {
  try {
    return window.localStorage.getItem(DESIGN_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Keeps `spec` as the design to open on next time. Answers whether it was kept.
 *
 * Synchronous, and on every change rather than on a timer: the write that matters most
 * is the one just before the buy button navigates to Stripe, and a debounced write
 * still waiting on its timer when the page goes is a write that never happened. A spec
 * is a kilobyte; writing it on every slider step costs less than drawing the step.
 */
export function saveDesign(spec: Spec): boolean {
  const record: SavedDesign = { version: DESIGN_RECORD_VERSION, spec };
  try {
    window.localStorage.setItem(DESIGN_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}
